import AlbumIcon from "@mui/icons-material/Album";
import AppleIcon from "@mui/icons-material/Apple";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { Box, Typography } from "@mui/material";
import { useEffect, useState, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { BaseModal } from "@/components/ui/BaseModal";
import { getOptimizedImageUrl, trackPlatformClick, type Platform } from "@/components/_helpers";
import { useCompanionPlaylist } from "@/hooks/useCompanionPlaylist";
import useYoutubeVideo from "@/hooks/useYoutubeVideo";
import { isConvexConfigured } from "@/lib/convex";
import { API_CACHE_TTL } from "@/utils/apiCache";
import { fetchAppleMusicEnrichment } from "@/utils/appleMusicEnrichment";

export interface SongLinksTarget {
  title: string;
  artist: string;
  artwork?: string;
  meta?: string;
  spotifyTrackId?: string;
  spotifyAlbumId?: string;
  albumName?: string;
  releasePath?: string;
}

interface SongLinksSheetProps {
  open: boolean;
  onClose: () => void;
  song: SongLinksTarget | null;
  extra?: ReactNode;
}

function CompanionLink({ albumId, children }: { albumId?: string; children: (playlist: { url: string; name: string } | null) => ReactNode }) {
  const playlist = useCompanionPlaylist(albumId);
  return <>{children(playlist ? { url: playlist.spotifyUrl, name: playlist.name } : null)}</>;
}

// One sheet for "where can I hear this song": a dialog on desktop and a bottom
// drawer on phones (BaseModal does the switch). Links are looked up only
// while it is open.
export function SongLinksSheet({ open, onClose, song, extra }: SongLinksSheetProps) {
  const [appleUrl, setAppleUrl] = useState<string | null>(null);
  const video = useYoutubeVideo(open && song ? song.artist : null, open && song ? song.title : null, [open, song?.artist, song?.title]);

  useEffect(() => {
    if (!open || !song) return;
    let cancelled = false;
    setAppleUrl(null);
    fetchAppleMusicEnrichment(
      { artistName: song.artist, releaseName: song.title, albumName: song.albumName, lookupType: "song", spotifyId: song.spotifyTrackId },
      { ttlMs: API_CACHE_TTL.SIX_HOURS },
    )
      .then((result) => {
        if (!cancelled) setAppleUrl(result?.url ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, song?.artist, song?.title, song?.albumName, song?.spotifyTrackId]);

  if (!song) return null;

  const spotifyUrl = song.spotifyTrackId
    ? `https://open.spotify.com/track/${song.spotifyTrackId}`
    : song.spotifyAlbumId
      ? `https://open.spotify.com/album/${song.spotifyAlbumId}`
      : null;

  const track = (platform: Platform, companionPlaylist = false) =>
    trackPlatformClick(platform, song.artist, song.title, { releaseName: song.albumName, releaseKey: song.spotifyAlbumId, companionPlaylist });

  const renderLinks = (playlist: { url: string; name: string } | null) => (
    <Box sx={{ display: "grid", gap: "10px" }}>
      {playlist ? (
        <LinkRow href={playlist.url} icon={<QueueMusicIcon />} label="Spotify playlist" detail={playlist.name} accent="#1ed760" onClick={() => track("spotify", true)} />
      ) : null}
      {spotifyUrl ? (
        <LinkRow href={spotifyUrl} icon={<PlayCircleIcon />} label="Listen on Spotify" detail={song.title} accent="#1ed760" onClick={() => track("spotify")} />
      ) : null}
      <LinkRow
        href={appleUrl ?? undefined}
        icon={<AppleIcon />}
        label="Apple Music"
        detail={appleUrl ? song.title : "Looking it up..."}
        accent="#fa4b64"
        onClick={() => track("apple")}
      />
      {video.data?.videoUrl ? (
        <LinkRow href={video.data.videoUrl} icon={<YouTubeIcon />} label="Watch the video" detail={video.data.videoTitle} accent="#ff3b3b" onClick={() => track("youtube")} />
      ) : video.loading ? (
        <LinkRow icon={<YouTubeIcon />} label="Video" detail="Looking it up..." accent="#ff3b3b" />
      ) : null}
      {song.releasePath ? (
        <Box
          component={RouterLink}
          to={song.releasePath}
          onClick={onClose}
          sx={{ ...rowSx, color: "var(--logoColor2)", "&:hover": { borderColor: "var(--logoColor)", color: "var(--logoColor2)" } }}
        >
          <Box sx={iconSx("var(--logoColor)")}>
            <AlbumIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={labelSx}>Song page</Typography>
            <Typography sx={detailSx}>Lyrics, downloads and the full release</Typography>
          </Box>
        </Box>
      ) : null}
    </Box>
  );

  return (
    <BaseModal
      isOpen={open}
      onClose={onClose}
      desktopModalMobileDrawer
      drawerBottomOffset="0px"
      drawerMaxHeight="calc(100dvh - 5rem)"
      bodySx={{
        backgroundColor: "var(--color5)",
        backgroundImage: "linear-gradient(180deg, color-mix(in srgb, var(--logoColor) 8%, transparent), transparent 40%)",
        border: "1px solid color-mix(in srgb, var(--logoColor) 18%, transparent)",
        borderRadius: { xs: "20px 20px 0 0", md: "22px" },
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)",
        width: { md: "min(440px, calc(100vw - 32px))" },
      }}
    >
      <Box sx={{ display: "grid", gap: "18px", p: { xs: "8px 18px calc(18px + env(safe-area-inset-bottom, 0px))", md: "24px" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {song.artwork ? (
            <Box
              component="img"
              src={getOptimizedImageUrl(song.artwork, { width: 240, height: 240, fit: "cover", quality: 80 })}
              alt={`${song.title} artwork`}
              sx={{ width: "96px", height: "96px", borderRadius: "14px", objectFit: "cover", flexShrink: 0, boxShadow: "0 18px 40px -18px rgba(0,0,0,0.8)" }}
            />
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: "12px", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--logoColor)", opacity: 0.8 }}>
              {song.meta || song.artist}
            </Typography>
            <Typography component="h2" sx={{ m: 0, fontFamily: "Hanson", fontWeight: 700, fontSize: "24px", lineHeight: 1.15, color: "var(--logoColor2)", overflowWrap: "anywhere" }}>
              {song.title}
            </Typography>
            <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", color: "var(--logoColor)" }}>{song.artist}</Typography>
          </Box>
        </Box>
        {isConvexConfigured ? <CompanionLink albumId={song.spotifyAlbumId}>{renderLinks}</CompanionLink> : renderLinks(null)}
        {extra}
      </Box>
    </BaseModal>
  );
}

const rowSx = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
  minHeight: "60px",
  p: "10px 14px",
  borderRadius: "14px",
  textDecoration: "none",
  border: "1px solid color-mix(in srgb, var(--logoColor) 20%, transparent)",
  backgroundColor: "color-mix(in srgb, var(--color5) 70%, transparent)",
  transition: "border-color 0.2s ease, transform 0.2s ease",
  "&:hover": { transform: "translateY(-1px)" },
  "&:focus-visible": { outline: "2px solid var(--logoColor2)", outlineOffset: "2px" },
} as const;

const iconSx = (accent: string) => ({
  width: "38px",
  height: "38px",
  borderRadius: "10px",
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  color: accent,
  backgroundColor: `color-mix(in srgb, ${accent} 14%, transparent)`,
  "& svg": { fontSize: "22px" },
});

const labelSx = { fontFamily: "Poppins, sans-serif", fontSize: "15px", fontWeight: 700, color: "var(--logoColor2)", lineHeight: 1.25 };
const detailSx = { fontFamily: "Poppins, sans-serif", fontSize: "13px", color: "var(--logoColor)", opacity: 0.85, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } as const;

function LinkRow({ href, icon, label, detail, accent, onClick }: { href?: string; icon: ReactNode; label: string; detail: string; accent: string; onClick?: () => void }) {
  const disabled = !href;
  return (
    <Box
      component={disabled ? "div" : "a"}
      href={href}
      target={disabled ? undefined : "_blank"}
      rel={disabled ? undefined : "noopener noreferrer"}
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled || undefined}
      sx={{ ...rowSx, opacity: disabled ? 0.55 : 1, pointerEvents: disabled ? "none" : "auto", "&:hover": { borderColor: accent, transform: "translateY(-1px)" } }}
    >
      <Box sx={iconSx(accent)}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={labelSx}>{label}</Typography>
        <Typography sx={detailSx}>{detail}</Typography>
      </Box>
    </Box>
  );
}

export default SongLinksSheet;
